/**
 * Subscription Access Service
 * Handles subscription-based module access control
 * Implements ACC-001 to ACC-010 test cases
 */

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiService } from './api.service';

export interface SubscriptionAccess {
  canAccess: boolean;
  status: string;
  isActive: boolean;
  isTrial: boolean;
  allowedModules: string[];
  restrictedModules: string[];
  domains: string[];
  subscriptionType?: string;
  /** From DB: free_user | trial_user | paid_user */
  subscriptionUserType?: string;
  daysRemaining?: number;
  trialDaysRemaining?: number;
}

@Injectable({
  providedIn: 'root'
})
export class SubscriptionAccessService {
  private subscriptionAccess$ = new BehaviorSubject<SubscriptionAccess | null>(null);
  private currentOrgId$ = new BehaviorSubject<number | null>(null);

  // Module mapping: route -> module name
  private moduleMapping: { [key: string]: string } = {
    '/dashboard': 'dashboard',
    '/company': 'company',
    '/materials': 'materials',
      '/machines': 'machines',
      '/machine-reservations': 'machines',
    '/clients': 'clients',
    '/projects': 'projects',
    '/pre-planning-projects': 'projects',
    '/design-stage-list': 'projects',
    '/planning-stage-list': 'projects',
    '/estimation-stage-list': 'projects',
    '/biding-list': 'projects',
    '/award-list': 'projects',
    '/purchase-order-list': 'projects',
    '/survey-list': 'projects',
    '/deviations-report-list': 'projects',
    '/po-amendment-list': 'projects',
    '/siv-list': 'projects',
    '/srv-list': 'projects',
    '/execution-list': 'projects',
    '/inspection-stage-list': 'projects',
    '/invoice-list': 'projects',
    '/monitoring-list': 'projects',
    '/closure-list': 'projects',
    '/bom': 'bom',
    '/ai-bom': 'ai_bom',
    '/boq': 'boq',
    '/ai-boq': 'ai_boq',
    '/project-bills': 'bills',
    '/payment-list': 'financial',
    '/work-orders': 'work_orders',
    '/employees': 'staff',
    '/attendance': 'staff',
    '/support-ticket': 'support',
    '/support-ticket/add': 'support',
    '/support-ticket-list': 'support',
    '/support-technical': 'support',
    '/support-technical/add': 'support',
    '/contact-us': 'support',
    '/alerts': 'alerts',
    '/gis': 'gis',
    '/asset-management': 'gis_assets',
    '/gis-mvp': 'gis_assets',
    '/home/gis-mvp': 'gis_assets',
    '/geospatial': 'gis',
    '/home/geospatial': 'gis',
    '/geospatial-dashboard': 'gis',
    '/home/geospatial-dashboard': 'gis',
    '/gis-project': 'gis',
    '/cadviewer': 'cad_viewer',
    '/integrations': 'integrations',
    '/integrations/clickup': 'integrations',
    '/integrations/primavera': 'integrations',
    '/integrations/microsoft-project': 'integrations',
    '/integrations/cad': 'integrations',
    '/road-boq-list': 'road_boq',
    '/road-boq': 'road_boq',
    '/segment-inspections': 'segment_inspection',
    '/segment-inspection': 'segment_inspection',
    '/documents': 'documents',
    '/reports': 'reports',
    '/analytics': 'analytics',
    '/labour-mobilization/mobilization-plans': 'resource_mobilization',
    '/labour-mobilization/daily-reports': 'resource_mobilization',
    '/labour-mobilization/deviation-report': 'resource_mobilization',
    '/work-breakdown-structure/deviation-report': 'work_breakdown_structure',
    '/work-breakdown-structure/list': 'work_breakdown_structure',
    '/work-breakdown-structure': 'work_breakdown_structure'
  };

  // Domain-specific module mapping
  private domainModuleMapping: { [key: string]: string[] } = {
    'Road': ['projects', 'bom', 'ai_bom', 'boq', 'ai_boq', 'road_boq', 'segment_inspection', 'gis_assets', 'bills', 'work_orders', 'staff', 'reports', 'resource_mobilization', 'work_breakdown_structure', 'integrations', 'gis', 'company', 'materials', 'machines', 'clients'],
    'Construction': ['projects', 'bom', 'ai_bom', 'boq', 'ai_boq', 'bills', 'work_orders', 'staff', 'reports', 'resource_mobilization', 'work_breakdown_structure', 'integrations', 'gis', 'company', 'materials', 'machines', 'clients'],
    'Utilities': ['projects', 'bom', 'ai_bom', 'boq', 'ai_boq', 'bills', 'work_orders', 'staff', 'reports', 'resource_mobilization', 'work_breakdown_structure', 'integrations', 'gis', 'company', 'materials', 'machines', 'clients'],
    'Consultancy': ['projects', 'bills', 'staff', 'reports', 'resource_mobilization', 'work_breakdown_structure', 'integrations', 'company', 'materials', 'machines', 'clients']
  };

  // Basic modules available to all (even inactive subscriptions)
  private basicModules: string[] = ['dashboard', 'subscription', 'support'];

  constructor(private apiService: ApiService) {
    this.loadSubscriptionAccess();
  }

  /**
   * Load subscription access from user session
   */
  loadSubscriptionAccess(): void {
    try {
      const userData = localStorage.getItem('user');
      if (!userData) {
        this.setBasicAccess();
        return;
      }

      const parsedData = JSON.parse(userData);
      
      // Handle different localStorage structures
      // Structure 1: { token, user: { org_id, subscription, ... } }
      // Structure 2: { user: { org_id, subscription, ... } }
      // Structure 3: { org_id, subscription, ... } (direct user data)
      
      let userDetails: any = null;
      let subscription: any = null;

      if (parsedData.user && typeof parsedData.user === 'object') {
        // Structure 1 or 2: user data is nested inside .user
        userDetails = parsedData.user;
        subscription = parsedData.user.subscription;
      } else if (parsedData.org_id || parsedData.subscription) {
        // Structure 3: user data is at root level
        userDetails = parsedData;
        subscription = parsedData.subscription;
      }

      // Also check user-level status details (from login response)
      const userStatusDetails = userDetails?.statusDetails;
      
      if (!subscription && !userStatusDetails) {
        this.setBasicAccess();
        return;
      }

      // Get status details from subscription or user level
      const statusDetails = subscription?.statusDetails || 
                           subscription?.membershipStatus || 
                           userStatusDetails;
      
      // ACC-001: Basic modules only for inactive subscription
      // ACC-002: All modules visible for active subscription
      const canAccess = statusDetails?.canAccess || false;
      const isActive = statusDetails?.isActive || false;

      // ACC-008: Check for trial plan
      const isTrial = this.isTrialPlan(subscription);
      const trialDaysRemaining = isTrial ? this.calculateTrialDaysRemaining(subscription) : undefined;

      // Get allowed/restricted modules
      const { allowedModules, restrictedModules } = this.getSubscriptionModules(subscription || {}, userDetails);

      // ACC-009: Domain-specific plan access
      const domains = this.getActiveDomains(subscription || {}, userDetails);

      // Get status from multiple possible sources
      const status = userDetails?.status || 
                    subscription?.status || 
                    statusDetails?.status || 
                    'Inactive';

      const access: SubscriptionAccess = {
        canAccess,
        status,
        isActive,
        isTrial,
        allowedModules,
        restrictedModules,
        domains,
        subscriptionType: subscription?.type,
        subscriptionUserType: subscription?.subscription_user_type,
        daysRemaining: statusDetails?.daysRemaining,
        trialDaysRemaining
      };

      console.log('Subscription Access Loaded:', access);
      this.subscriptionAccess$.next(access);
      this.currentOrgId$.next(userDetails?.org_id || null);
    } catch (error) {
      console.error('Error loading subscription access:', error);
      this.setBasicAccess();
    }
  }

  /**
   * Set basic access (ACC-001)
   */
  private setBasicAccess(): void {
    const access: SubscriptionAccess = {
      canAccess: false,
      status: 'No Subscription',
      isActive: false,
      isTrial: false,
      allowedModules: this.basicModules,
      restrictedModules: [],
      domains: []
    };
    this.subscriptionAccess$.next(access);
  }

  /**
   * Check if subscription is trial plan (ACC-008)
   */
  private isTrialPlan(subscription: any): boolean {
    if (!subscription) return false;

    if (subscription.subscription_user_type === 'trial_user') {
      return true;
    }
    
    // Check metadata for trial flag
    if (subscription.metadata) {
      try {
        const metadata = typeof subscription.metadata === 'string' 
          ? JSON.parse(subscription.metadata) 
          : subscription.metadata;
        return metadata.is_trial === true || metadata.plan_type === 'trial';
      } catch (e) {
        // Ignore parse errors
      }
    }

    // Check subscription type
    return subscription.type === 'trial' || subscription.type === 'Trial';
  }

  /**
   * Calculate trial days remaining (ACC-008)
   */
  private calculateTrialDaysRemaining(subscription: any): number {
    if (!subscription.membership_end_date) return 0;
    
    const endDate = new Date(subscription.membership_end_date);
    const today = new Date();
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(0, diffDays);
  }

  /**
   * Get subscription modules (ACC-001, ACC-002, ACC-004, ACC-005)
   */
  private getSubscriptionModules(subscription: any, userDetails: any): { allowedModules: string[], restrictedModules: string[] } {
    const allModules = [
      'dashboard',
      'projects',
      'bom',
      'ai_bom',
      'boq',
      'ai_boq',
      'bills',
      'work_orders',
      'staff',
      'financial',
      'support',
      'reports',
      'analytics',
      'gis',
      'gis_assets',
      'integrations',
      'road_boq',
      'segment_inspection',
      'cad_viewer',
      'documents',
      'alerts',
      'subscription',
      'resource_mobilization',
      'work_breakdown_structure',
      'company',
      'materials',
      'machines',
      'clients'
    ];

    // ACC-001: If subscription is inactive, only basic modules
    const statusDetails = subscription.statusDetails || subscription.membershipStatus;
    if (!statusDetails?.canAccess || !statusDetails?.isActive) {
      return {
        allowedModules: this.basicModules,
        restrictedModules: allModules.filter(m => !this.basicModules.includes(m))
      };
    }

    // Get restricted modules from metadata
    let restrictedModules: string[] = [];
    if (subscription.metadata) {
      try {
        const metadata = typeof subscription.metadata === 'string' 
          ? JSON.parse(subscription.metadata) 
          : subscription.metadata;
        restrictedModules = (metadata.restricted_modules || []).map((m: string) => m.toLowerCase());
      } catch (e) {
        // Ignore parse errors
      }
    }

    // ACC-003: Role-based restrictions (L3 vendor + Basic plan)
    if (userDetails?.role_id === 3 && this.isBasicPlan(subscription)) {
      // L3 vendors on basic plan only see vendor-specific modules
      restrictedModules = allModules.filter(m => 
        !['dashboard', 'support', 'subscription'].includes(m)
      );
    }

    // ACC-009: Domain-specific restrictions
    const domains = this.getActiveDomains(subscription, userDetails);
    if (domains.length > 0) {
      // Get domain-specific allowed modules
      const domainAllowedModules = new Set<string>();
      domains.forEach(domain => {
        const domainModules = this.domainModuleMapping[domain] || [];
        domainModules.forEach(mod => domainAllowedModules.add(mod));
      });
      
      // Add basic modules
      this.basicModules.forEach(mod => domainAllowedModules.add(mod));

      // Restrict modules not in domain
      const domainRestricted = allModules.filter(m => !domainAllowedModules.has(m));
      restrictedModules = [...new Set([...restrictedModules, ...domainRestricted])];
    }

    const allowedModules = allModules.filter(m => 
      !restrictedModules.includes(m.toLowerCase())
    );

    return { allowedModules, restrictedModules };
  }

  /**
   * Check if subscription is basic plan
   */
  private isBasicPlan(subscription: any): boolean {
    if (!subscription) return false;
    
    if (subscription.metadata) {
      try {
        const metadata = typeof subscription.metadata === 'string' 
          ? JSON.parse(subscription.metadata) 
          : subscription.metadata;
        return metadata.plan_type === 'basic' || metadata.plan_level === 'basic';
      } catch (e) {
        // Ignore parse errors
      }
    }

    return subscription.type === 'basic' || subscription.type === 'Basic';
  }

  /**
   * Get active domains from subscription (ACC-009)
   */
  private parseSubscriptionDomainField(field: unknown): Record<string, unknown> | null {
    if (field == null) return null;
    if (typeof field === 'string') {
      try {
        const parsed = JSON.parse(field);
        return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : null;
      } catch {
        return null;
      }
    }
    return typeof field === 'object' ? field as Record<string, unknown> : null;
  }

  private domainFieldHasEntitlement(field: unknown): boolean {
    const data = this.parseSubscriptionDomainField(field);
    if (!data) return false;
    const count = parseInt(String(data['users_count'] ?? ''), 10);
    return Number.isFinite(count) && count > 0;
  }

  hasRoadDomainSubscription(): boolean {
    const access = this.subscriptionAccess$.value;
    if (!access) return false;
    return access.domains.some((d) => d.toLowerCase() === 'road');
  }

  private getActiveDomains(subscription: any, userDetails: any): string[] {
    const domains: string[] = [];

    // Get domains from subscription if available
    if (subscription) {
      if (this.domainFieldHasEntitlement(subscription.road)) {
        domains.push('Road');
      }
      if (this.domainFieldHasEntitlement(subscription.construction)) {
        domains.push('Construction');
      }
      if (this.domainFieldHasEntitlement(subscription.utilities)) {
        domains.push('Utilities');
      }
      if (this.domainFieldHasEntitlement(subscription.consultancy)) {
        domains.push('Consultancy');
      }
    }

    // Fallback to user domain if subscription doesn't have domain info
    if (domains.length === 0 && userDetails?.domain) {
      const userDomains = typeof userDetails.domain === 'string' 
        ? userDetails.domain.split(',').map((d: string) => d.trim()).filter((d: string) => d)
        : Array.isArray(userDetails.domain) ? userDetails.domain : [];
      domains.push(...userDomains);
    }

    return domains;
  }

  /**
   * Check if user can access a module
   */
  canAccessModule(moduleName: string): boolean {
    const access = this.subscriptionAccess$.value;
    if (!access) return false;

    // Admin always has access
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const parsedData = JSON.parse(userData);
        // Handle nested structure: { token, user: {...} }
        const userDetails = parsedData.user || parsedData;
        
        const rn = typeof userDetails?.role_name === 'string' ? userDetails.role_name.toLowerCase() : '';
        if (rn === 'admin' || rn === 'super admin' || userDetails?.type === 1 || userDetails?.role_id === 1) {
          return true;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }

    return access.allowedModules.includes(moduleName.toLowerCase());
  }

  /**
   * Check if user can access a route
   */
  canAccessRoute(route: string): boolean {
    const normalized = route.replace(/^\/home/, '').replace(/\/$/, '') || '/dashboard';

    if (this.moduleMapping[normalized]) {
      return this.canAccessModule(this.moduleMapping[normalized]);
    }

    const patterns = Object.keys(this.moduleMapping).sort((a, b) => b.length - a.length);
    for (const pattern of patterns) {
      if (normalized === pattern || normalized.startsWith(`${pattern}/`)) {
        return this.canAccessModule(this.moduleMapping[pattern]);
      }
    }

    const moduleName = normalized.replace(/^\//, '').split('/')[0];
    return this.canAccessModule(moduleName);
  }

  /**
   * Get current subscription access
   */
  getSubscriptionAccess(): Observable<SubscriptionAccess | null> {
    return this.subscriptionAccess$.asObservable();
  }

  /**
   * Get current subscription access value
   */
  getCurrentAccess(): SubscriptionAccess | null {
    return this.subscriptionAccess$.value;
  }

  /**
   * Refresh subscription access (ACC-004, ACC-005, ACC-006, ACC-007)
   */
  refreshSubscriptionAccess(): void {
    // Reload from localStorage
    this.loadSubscriptionAccess();

    // Optionally fetch fresh data from API
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const parsedData = JSON.parse(userData);
        // Handle nested structure: { token, user: {...} }
        const userDetails = parsedData.user || parsedData;
        const orgId = userDetails?.org_id;
        
        if (orgId) {
          this.apiService.getSubscriptionDetails({ organization_id: orgId }).subscribe({
            next: (res: any) => {
              // Update localStorage with fresh subscription data
              try {
                const currentData = JSON.parse(localStorage.getItem('user') || '{}');
                
                // Determine the subscription data from response
                const newSubscription = Array.isArray(res) ? res[0] : res;
                
                // Update the correct location based on structure
                if (currentData.user) {
                  // Structure: { token, user: {...} }
                  currentData.user.subscription = newSubscription;
                  // Also update status details if available
                  if (newSubscription?.statusDetails) {
                    currentData.user.statusDetails = newSubscription.statusDetails;
                    currentData.user.status = newSubscription.status || newSubscription.statusDetails?.status;
                  }
                } else {
                  // Structure: direct user data
                  currentData.subscription = newSubscription;
                  if (newSubscription?.statusDetails) {
                    currentData.statusDetails = newSubscription.statusDetails;
                    currentData.status = newSubscription.status || newSubscription.statusDetails?.status;
                  }
                }
                
                localStorage.setItem('user', JSON.stringify(currentData));
                this.loadSubscriptionAccess();
              } catch (e) {
                console.error('Error updating subscription data:', e);
              }
            },
            error: (err) => {
              console.error('Error refreshing subscription access:', err);
            }
          });
        }
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }
  }

  /**
   * Switch organization (ACC-010)
   */
  switchOrganization(orgId: number): void {
    this.currentOrgId$.next(orgId);
    // Reload subscription access for new organization
    this.refreshSubscriptionAccess();
  }

  /**
   * Get current organization ID
   */
  getCurrentOrgId(): Observable<number | null> {
    return this.currentOrgId$.asObservable();
  }
}
